import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Globe2, MapPin, Pencil, Plus, RefreshCw, Trash2, Copy, Wand2, Search, Download } from 'lucide-react'
import {
  useLocations,
  useCreateLocation,
  useUpdateLocation,
  useDeleteLocation,
  useCopyLocationPricing,
  useBulkPricing,
  useLocationCountries,
  useLocationRegions,
} from '@/hooks/data/useLocations'
import type { Location, LocationInput } from '@/types/ecommerce'
import EditModal from '@/components/crud/EditModal'
import SuburbsModal from '@/components/crud/SuburbsModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'

function BulkPricingPanel({ locations }: { locations: Location[] }) {
  const bulk = useBulkPricing()
  const [selected, setSelected] = useState<string[]>([])
  const [adjustmentType, setAdjustmentType] = useState<'percentage' | 'fixed'>('percentage')
  const [value, setValue] = useState('10')
  const [applyToShipping, setApplyToShipping] = useState(true)
  const [applyToBasePrice, setApplyToBasePrice] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const apply = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one location')
      return
    }
    try {
      const res = await bulk.mutateAsync({
        locationIds: selected,
        adjustmentType,
        value: Number(value) || 0,
        applyToShipping,
        applyToBasePrice,
      })
      setResult(`Updated ${res.rowsUpdated} pricing rows across ${res.productsAffected} products × ${res.locationsAffected} locations.`)
      toast.success('Bulk pricing applied')
    } catch (err) {
      toast.error(userMessage(err))
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center gap-2">
        <Wand2 className="h-4 w-4 text-gray-500" />
        <h2 className="font-semibold text-gray-800 dark:text-white">Bulk Location Pricing</h2>
      </div>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Applies to every currently-Published product's location override for the selected locations (creates one if it doesn't exist yet).
      </p>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {locations.map((l) => (
          <button
            key={l.id}
            onClick={() => toggle(l.id)}
            className={`rounded-full border px-2.5 py-1 text-xs ${
              selected.includes(l.id)
                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'
            }`}
          >
            {l.code}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-gray-500">Adjustment</label>
          <select
            value={adjustmentType}
            onChange={(e) => setAdjustmentType(e.target.value as 'percentage' | 'fixed')}
            className="mt-1 block rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed amount</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Value ({adjustmentType === 'percentage' ? '%' : '$'})</label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            type="number"
            className="mt-1 block w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={applyToShipping} onChange={(e) => setApplyToShipping(e.target.checked)} /> Shipping cost
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={applyToBasePrice} onChange={(e) => setApplyToBasePrice(e.target.checked)} /> Base price
        </label>
        <button
          onClick={apply}
          disabled={bulk.isPending}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          Apply
        </button>
      </div>
      {result && <p className="mt-3 text-xs text-green-600 dark:text-green-400">{result}</p>}
    </div>
  )
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function locationsToCsv(locations: Location[]): string {
  const esc = (v: string) => (v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v)
  const header = 'Code,Name,Country,Region,ShippingCost,FreeShippingThreshold,DeliveryTime,Active'
  const rows = locations.map((l) =>
    [
      esc(l.code), esc(l.name), esc(l.country), esc(l.regionName ?? ''),
      l.defaultShippingCost.toFixed(2), l.defaultFreeShippingThreshold?.toFixed(2) ?? '',
      esc(l.deliveryTimeEstimate ?? ''), l.active,
    ].join(','),
  )
  return [header, ...rows].join('\n')
}

function LocationsPage() {
  const { data: locations = [], isLoading, isFetching, refetch } = useLocations(true)
  const create = useCreateLocation()
  const update = useUpdateLocation()
  const del = useDeleteLocation()
  const copyPricing = useCopyLocationPricing()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Location | null>(null)
  const [copyingFrom, setCopyingFrom] = useState<Location | null>(null)
  const [copyTarget, setCopyTarget] = useState('')
  const [managingSuburbs, setManagingSuburbs] = useState<Location | null>(null)

  const [search, setSearch] = useState('')
  const [filterCountry, setFilterCountry] = useState('')
  const [filterRegionId, setFilterRegionId] = useState('')

  const { data: countries = [] } = useLocationCountries()
  const { data: regions = [] } = useLocationRegions()
  const regionOptions = countries.flatMap((c) =>
    regions.filter((r) => r.countryId === c.id).map((r) => ({ value: r.id, label: `${c.name} — ${r.name}` })),
  )
  // Filter panel's Region options narrow to the selected Country only — same source list,
  // just scoped, since picking a region from a different country than the one chosen would
  // always return zero rows.
  const filterRegionOptions = filterCountry
    ? regions.filter((r) => countries.find((c) => c.id === r.countryId)?.code === filterCountry)
    : regions

  const filteredLocations = useMemo(() => {
    const q = search.trim().toLowerCase()
    return locations.filter((l) => {
      if (q && !l.name.toLowerCase().includes(q) && !l.code.toLowerCase().includes(q)) return false
      if (filterCountry && l.country !== filterCountry) return false
      if (filterRegionId && l.regionId !== filterRegionId) return false
      return true
    })
  }, [locations, search, filterCountry, filterRegionId])

  const fields = [
    { key: 'code', label: 'Code', required: true, placeholder: 'AUK', hint: 'Short unique code, e.g. AUK, WLG' },
    { key: 'name', label: 'Name', required: true },
    {
      key: 'country', label: 'Country', type: 'select' as const, required: true,
      options: [{ value: 'NZ', label: 'New Zealand' }], // the store ships within New Zealand only
    },
    {
      key: 'regionId', label: 'Region', type: 'select' as const,
      options: [{ value: '', label: '— None —' }, ...regionOptions],
      hint: 'Real Country → Region hierarchy (for browsing/filtering) — the Country field above still drives the legacy pricing lookup.',
    },
    { key: 'defaultShippingCost', label: 'Default Shipping Cost ($)', type: 'number' as const, required: true },
    { key: 'defaultFreeShippingThreshold', label: 'Free Shipping Threshold ($)', type: 'number' as const, hint: 'Leave blank to disable free shipping for this location' },
    { key: 'deliveryTimeEstimate', label: 'Delivery Time', placeholder: '2-3 days' },
    { key: 'sortOrder', label: 'Sort Order', type: 'number' as const, required: true },
    { key: 'active', label: 'Active', type: 'select' as const, required: true, options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
  ]

  const toInput = (values: Record<string, any>): LocationInput => ({
    code: values.code,
    name: values.name,
    country: values.country,
    regionId: values.regionId || null,
    defaultShippingCost: Number(values.defaultShippingCost) || 0,
    defaultFreeShippingThreshold: values.defaultFreeShippingThreshold !== '' && values.defaultFreeShippingThreshold != null
      ? Number(values.defaultFreeShippingThreshold) : null,
    deliveryTimeEstimate: values.deliveryTimeEstimate || '',
    active: values.active === 'true',
    sortOrder: Number(values.sortOrder) || 0,
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <MapPin className="w-6 h-6" /> Locations & Shipping
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Real New Zealand regions (LocationsController) — orders are priced/shipped using the customer's actual shipping address region, falling back to Site Settings' global default when unmatched.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadBlob(new Blob([locationsToCsv(filteredLocations)], { type: 'text/csv' }), 'locations.csv')}
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Location
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or code…"
            className="pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm w-56"
          />
        </div>
        <select
          value={filterCountry}
          onChange={(e) => { setFilterCountry(e.target.value); setFilterRegionId('') }}
          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
        >
          <option value="">All countries</option>
          {countries.map((c) => <option key={c.id} value={c.code}>{c.name}</option>)}
        </select>
        <select
          value={filterRegionId}
          onChange={(e) => setFilterRegionId(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
        >
          <option value="">All regions</option>
          {filterRegionOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {(search || filterCountry || filterRegionId) && (
          <>
            <span className="text-xs text-gray-400">{filteredLocations.length} of {locations.length}</span>
            <button
              onClick={() => { setSearch(''); setFilterCountry(''); setFilterRegionId('') }}
              className="text-xs text-blue-600 hover:underline"
            >
              Clear filters
            </button>
          </>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading locations…</p>
        ) : filteredLocations.length === 0 ? (
          <p className="p-6 text-gray-500 text-sm">No locations match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                <tr>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Location</th>
                  <th className="px-4 py-2">Region</th>
                  <th className="px-4 py-2">Shipping Cost</th>
                  <th className="px-4 py-2">Free Shipping Over</th>
                  <th className="px-4 py-2">Delivery Time</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((l) => (
                  <tr key={l.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-3 font-mono text-xs">{l.code}</td>
                    <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
                      {l.name} <span className="text-xs text-gray-400">({l.country})</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {l.regionName ? `${l.countryName} — ${l.regionName}` : '—'}
                    </td>
                    <td className="px-4 py-3">${l.defaultShippingCost.toFixed(2)}</td>
                    <td className="px-4 py-3">{l.defaultFreeShippingThreshold != null ? `$${l.defaultFreeShippingThreshold.toFixed(2)}` : '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{l.deliveryTimeEstimate || '—'}</td>
                    <td className="px-4 py-3">
                      <InlineToggle
                        checked={l.active}
                        label={l.active ? 'Active' : 'Inactive'}
                        onToggle={(next) =>
                          update.mutateAsync({
                            id: l.id,
                            body: {
                              code: l.code,
                              name: l.name,
                              country: l.country,
                              regionId: l.regionId ?? null,
                              defaultShippingCost: l.defaultShippingCost,
                              defaultFreeShippingThreshold: l.defaultFreeShippingThreshold,
                              deliveryTimeEstimate: l.deliveryTimeEstimate ?? '',
                              active: next,
                              sortOrder: l.sortOrder,
                            },
                          })
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setManagingSuburbs(l)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Manage suburbs">
                          <Globe2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setCopyingFrom(l); setCopyTarget('') }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Copy pricing to another location">
                          <Copy className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditing(l)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit location">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            confirmDelete(l.name, async () => {
                              try {
                                await del.mutateAsync(l.id)
                              } catch (err) {
                                toast.error(userMessage(err))
                              }
                            })
                          }
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                          title="Delete location"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BulkPricingPanel locations={locations.filter((l) => l.active)} />

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Location"
        fields={fields}
        initialValues={{ country: 'NZ', active: 'true', sortOrder: locations.length }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Location"
        description={editing?.name}
        fields={fields.map((f) => (f.key === 'code' ? { ...f, hint: 'Code cannot be changed after creation' } : f))}
        initialValues={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                country: editing.country,
                regionId: editing.regionId ?? '',
                defaultShippingCost: editing.defaultShippingCost,
                defaultFreeShippingThreshold: editing.defaultFreeShippingThreshold ?? '',
                deliveryTimeEstimate: editing.deliveryTimeEstimate,
                sortOrder: editing.sortOrder,
                active: editing.active ? 'true' : 'false',
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />

      {copyingFrom && (
        <EditModal
          isOpen={!!copyingFrom}
          onClose={() => setCopyingFrom(null)}
          title={`Copy pricing from ${copyingFrom.name}`}
          description="Copies default shipping cost, free-shipping threshold and delivery time onto the target location."
          fields={[
            {
              key: 'targetId', label: 'Target location', type: 'select' as const, required: true,
              options: locations.filter((l) => l.id !== copyingFrom.id).map((l) => ({ value: l.id, label: `${l.name} (${l.code})` })),
            },
          ]}
          initialValues={{ targetId: copyTarget }}
          submitLabel="Copy"
          onSubmit={async (values) => {
            try {
              await copyPricing.mutateAsync({ id: copyingFrom.id, targetId: values.targetId })
              toast.success(`Copied ${copyingFrom.name}'s pricing`)
            } catch (err) {
              toast.error(userMessage(err))
              throw err
            }
          }}
        />
      )}

      <SuburbsModal
        isOpen={!!managingSuburbs}
        onClose={() => setManagingSuburbs(null)}
        locationId={managingSuburbs?.id ?? null}
        locationName={managingSuburbs?.name}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/locations/')({
  component: LocationsPage,
})
